import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { User, LogOut, Calendar, Menu, X } from 'lucide-react';
import { getAchievementLevel } from '../utils/achievements';

const Navbar = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Don't show auth buttons while loading
  const showAuthButtons = !loading;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-border/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <span className="text-white font-display text-sm">1</span>
            </div>
            <span className="font-display text-xl text-foreground">One Day Fast</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/" 
              className="text-muted-foreground hover:text-foreground transition-colors font-body text-sm"
              data-testid="nav-calendar"
            >
              Calendar
            </Link>
            <Link 
              to="/about" 
              className="text-muted-foreground hover:text-foreground transition-colors font-body text-sm"
              data-testid="nav-about"
            >
              About
            </Link>
            
            {showAuthButtons && user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2"
                    data-testid="nav-user-menu"
                  >
                    <div className="relative">
                      {(() => {
                        const level = getAchievementLevel(user.days_fasted || 0);
                        return (
                          <>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center overflow-hidden ring-2 ${level.color.replace('bg-', 'ring-')}/50`}
                                 style={{ backgroundColor: user.profile_picture ? 'transparent' : `hsl(var(--primary) / 0.1)` }}>
                              {user.profile_picture ? (
                                <img 
                                  src={user.profile_picture} 
                                  alt={user.display_name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <span className="text-primary font-medium text-sm">
                                  {user.display_name.charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                            {(user.days_fasted || 0) > 0 && (
                              <div className={`absolute -bottom-1 -right-1 ${level.color} text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center border border-white`}>
                                {user.days_fasted > 99 ? '99+' : user.days_fasted}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <span className="text-sm font-medium">{user.display_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="nav-profile">
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-fasts')} data-testid="nav-my-fasts">
                    <Calendar className="mr-2 h-4 w-4" />
                    My Fasts
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : showAuthButtons ? (
              <div className="flex items-center gap-3">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                  data-testid="nav-login"
                >
                  Log in
                </Button>
                <Button 
                  onClick={() => navigate('/register')}
                  className="bg-primary hover:bg-primary/90 text-white rounded-full px-6"
                  data-testid="nav-register"
                >
                  Join
                </Button>
              </div>
            ) : null}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="nav-mobile-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border/50 animate-fade-in">
            <div className="flex flex-col gap-3">
              <Link 
                to="/" 
                className="px-3 py-2 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Calendar
              </Link>
              <Link 
                to="/about" 
                className="px-3 py-2 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </Link>
              {user ? (
                <>
                  <Link 
                    to="/profile" 
                    className="px-3 py-2 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Profile
                  </Link>
                  <Link 
                    to="/my-fasts" 
                    className="px-3 py-2 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    My Fasts
                  </Link>
                  <button 
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="px-3 py-2 text-left text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="px-3 py-2 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Log in
                  </Link>
                  <Link 
                    to="/register" 
                    className="px-3 py-2 text-primary font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Join
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
